import { useRef } from "react";

import {
  ConnectionBaseFragment,
  IntegrationConfigurationFragment,
  useConnectionLazyQuery,
  useConnectionsLazyQuery,
  useCreateConnectionMutation,
} from "@/apollo/types";
import { toBase64 } from "@/utils/encoding";

import { useOAuth } from "../oauth";

type ConnectionOptions = {
  onSuccess: (connection: ConnectionBaseFragment) => void;
  onError: (error: Error, integrationId: string | undefined) => void;
};

export function useCreateConnection(options: ConnectionOptions) {
  const integrationIdContextRef = useRef<string>();
  const [fetchConnections] = useConnectionsLazyQuery();

  const [createConnection, { loading }] = useCreateConnectionMutation({
    awaitRefetchQueries: true,
    onCompleted: async (data) => {
      await fetchConnections({
        fetchPolicy: "network-only",
      });
      options.onSuccess(data.createConnection);
    },
    onError: (e) => {
      options.onError(e, integrationIdContextRef.current);
    },
  });

  const [fetchConnection] = useConnectionLazyQuery();

  const { handleOAuth, oAuthLoading } = useOAuth({
    onSuccess: async (connectionId) => {
      await fetchConnections({
        fetchPolicy: "network-only",
      });
      const { data } = await fetchConnection({
        variables: {
          id: connectionId,
        },
      });
      if (data?.connection == null) {
        options.onError(
          new Error("Connection not found"),
          integrationIdContextRef.current,
        );
        return;
      }
      options.onSuccess(data.connection);
    },
    onError: (error) => {
      options.onError(error, integrationIdContextRef.current);
    },
  });

  return {
    loading: loading || oAuthLoading,
    createConnection: async (
      integration: IntegrationConfigurationFragment,
      data: Record<string, any> & { label: string },
    ) => {
      integrationIdContextRef.current = integration.id;
      const { label, selectedForm, ...rest } = data;

      if (selectedForm.type === "oauth") {
        return handleOAuth({
          integrationId: integration.id,
          data,
          formId: selectedForm.id,
        });
      }

      return createConnection({
        variables: {
          integrationId: integration.id,
          label,
          payload: toBase64(rest),
          formId: selectedForm.id,
        },
      });
    },
  };
}
